import { Button } from "@material-ui/core"
import { Contact, postContactEcommContact } from "api"
import { trackCallMeBack, trackPersonalDetails } from "common/analytics"
import { getUtmParamsAsKeyValueList } from "common/bit/utils/utmParams"
import FormikCheckboxField from "common/FormikCheckboxField"
import { useIsDesktop } from "common/MediaQueries"
import { postCallboxLead } from "common/postCallboxLead"
import UseFocusFirstInvalidField from "common/UseFocusFirstInvalidField"
import ShowRoomSelect from "components/shopFeatures/auth/register/ShowRoomSelect"
import { Field, Form, Formik, FormikProps } from "formik"
import { TextField } from "formik-material-ui"
import { useEffect, useMemo, useRef } from "react"
import { useTypedSelector } from "store"
import styled from "styled-components"
import * as yup from "yup"

const StyledForm = styled(Form)`
  text-align: right;
`
const StyledButton = styled(Button)`
  margin-top: 30px;
`

const schema = yup
  .object({
    fullName: yup
      .string()
      .required("שדה חובה")
      .max(50, "ניתן להזין עד 50 תווים")
      .matches(/([a-zA-Zא-ת'"\\.-/,].+\s).+/, "שם מלא לא תקין"),
    phone: yup
      .string()
      .required("שדה חובה")
      .matches(/^05\d-?\d{7}$/, "מספר נייד שגוי"),
    showRoom: yup.number().positive("יש לבחור אולם תצוגה").defined(),
    agreeMarketingOffers: yup.boolean().defined(),
  })
  .defined()

const initialValues: yup.InferType<typeof schema> = {
  fullName: "",
  phone: "",
  showRoom: 0,
  agreeMarketingOffers: false,
}

type ContactFormProps = {
  onSubmit: (success: boolean) => void
}

const ContactForm = ({ onSubmit }: ContactFormProps) => {
  const fullNameRef = useRef<HTMLInputElement>(null)
  const mobilePhoneRef = useRef<HTMLInputElement>(null)
  const termsRef = useRef<HTMLButtonElement>(null)

  const fieldNameInputRefMap = useMemo(
    () => ({
      fullName: fullNameRef,
      phone: mobilePhoneRef,
      terms: termsRef,
    }),
    []
  )

  const bagRef = useRef<FormikProps<typeof initialValues>>()

  const { model } = useTypedSelector((state) => state.wizard)
  const { userInfo: registeredUserInfo } = useTypedSelector(
    (state) => state.auth
  )

  const isDesktop = useIsDesktop()

  useEffect(() => {
    if (!registeredUserInfo) return

    bagRef.current?.setValues({
      fullName: `${registeredUserInfo.firstName}  ${registeredUserInfo.lastName}`,
      phone: registeredUserInfo.mobilePhone,
      showRoom: registeredUserInfo.showRoom,
      agreeMarketingOffers: false,
    })
  }, [registeredUserInfo])

  return (
    <Formik
      innerRef={bagRef as any}
      validationSchema={schema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values) => {
        const utmValues = getUtmParamsAsKeyValueList()
        const request: Contact = {
          fullName: values.fullName,
          phone: values.phone,
          description: "",
          mediaChannelId: "",
          branchId: values.showRoom,
          modelId: model?.id ?? null,
          platform: isDesktop ? "Pc" : "Mobile",
          isMailing: values.agreeMarketingOffers,
          utmValues,
          emailAddress: registeredUserInfo?.emailAddress || "",
          sid: window?.__ctm?.config?.sid || "",
          formName: "Mazda_meeting_form",
        }

        const { data } = await postContactEcommContact(request)
        postCallboxLead("contact", request)

        if (data.body) {
          trackCallMeBack()
          trackPersonalDetails(
            "ContactUs",
            registeredUserInfo?.emailAddress || "",
            values.phone,
            true
          )
        }
        // if error server return null, then return false
        onSubmit(data.body ?? false)
      }}
    >
      {({ isSubmitting }) => (
        <>
          <UseFocusFirstInvalidField
            fieldNameInputRefMap={fieldNameInputRefMap}
          />
          <StyledForm noValidate>
            <Field
              name="fullName"
              component={TextField}
              label="שם מלא"
              autoComplete="name"
              id="name"
              inputRef={fullNameRef}
              required
              helperText=" " // prevent a "jump" when an error appears
            />

            <Field
              name="phone"
              id="phone"
              component={TextField}
              type="tel"
              label="מספר נייד"
              autoComplete="tel-national"
              required
              inputRef={mobilePhoneRef}
              helperText=" " // prevent a "jump" when an error appears
              inputProps={{ maxLength: 11 }}
            />

            <ShowRoomSelect />

            <FormikCheckboxField
              inputRef={termsRef}
              name="agreeMarketingOffers"
              label='הנני מאשר/ת קבלת פניות שיווקיות לדוא"ל, פקס, סמס מקבוצת דלק מוטורס ולהכלל במאגר המידע של הקבוצה'
            />

            <StyledButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              fullWidth
            >
              שליחה
            </StyledButton>
          </StyledForm>
        </>
      )}
    </Formik>
  )
}

export default ContactForm
